import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  FunnelChart, Funnel, LabelList, Cell
} from 'recharts';
import { FiSend, FiCheckCircle, FiXCircle, FiClock, FiArrowUpRight } from 'react-icons/fi';

/* ── Animated Number Counter ── */
const AnimatedNumber = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const numVal = typeof value === 'string' ? parseInt(value.replace(/,/g, ''), 10) : value;
  useEffect(() => {
    if (isNaN(numVal) || numVal === 0) { setDisplay(0); return; }
    let start = 0;
    const startTime = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      start = Math.floor(eased * numVal);
      setDisplay(start);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [numVal, duration]);
  return <>{display.toLocaleString()}</>;
};

/* ── Scroll Reveal Hook ── */
const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const C = {
  sky: '#7ea52e', purple: '#1a1a18', green: '#7ea52e',
  orange: '#9ec25a', red: '#1a1a18', pink: '#5c7a1f', cyan: '#b7d083', slate: '#c9c9c3'
};

const TYPE_COLORS = {
  whatsapp: '#7ea52e', whatsapp_image: '#6b8f27', whatsapp_video: '#57721f',
  whatsapp_document: '#455a19', sms: '#9ec25a', email: '#1a1a18',
  rcs: '#b7d083', newsletter: '#5c7a1f', push_notification: '#7e7e71',
  voice_call: '#abab9f', status_messaging: '#161614', custom: '#c9c9c3'
};
const TYPE_LABELS = {
  whatsapp: 'WhatsApp', whatsapp_image: 'WA Image', whatsapp_video: 'WA Video',
  whatsapp_document: 'WA Doc', sms: 'SMS', email: 'Email',
  rcs: 'RCS', newsletter: 'Newsletter', push_notification: 'Push',
  voice_call: 'Voice', status_messaging: 'Status', custom: 'Custom'
};

/* ── Glass Tooltip ── */
const GlassTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)',
      border: '1px solid rgba(126,165,46,0.12)', borderRadius: 14,
      padding: '12px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    }}>
      <p style={{ color: '#0f172a', fontSize: 12, fontWeight: 700, marginBottom: 6, fontFamily: 'Poppins, sans-serif' }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || p.fill }} />
          <span style={{ color: '#475569', fontSize: 12 }}>{p.name}:</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

const CustomLegend = ({ payload }) => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: 20, paddingTop: 8 }}>
    {payload?.map((e, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: e.color }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{e.value}</span>
      </div>
    ))}
  </div>
);

/* ── Shared SVG Chart Tooltip ── */
const DoughnutTooltip = ({ tip }) => {
  if (!tip) return null;
  return (
    <div style={{
      position: 'absolute', pointerEvents: 'none', zIndex: 50,
      background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(16px)',
      border: '1px solid rgba(126,165,46,0.15)', borderRadius: 12,
      padding: '10px 14px', boxShadow: '0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(126,165,46,0.06)',
      fontSize: 12, fontFamily: 'Poppins, sans-serif', whiteSpace: 'nowrap',
      left: tip.x + 12, top: tip.y - 10, opacity: 1, minWidth: 90,
    }}>
      {tip.label && <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 4, letterSpacing: 0.3 }}>{tip.label}</div>}
      {tip.items && tip.items.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 1 }}>
          {item.color && <span style={{ width: 7, height: 7, borderRadius: '50%', background: item.color, flexShrink: 0 }} />}
          <span style={{ color: '#475569' }}>{item.name}:</span>
          <span style={{ fontWeight: 700, color: '#0f172a' }}>{typeof item.value === 'number' ? item.value.toLocaleString() : item.value}</span>
        </div>
      ))}
    </div>
  );
};

/* ── 3D Doughnut Chart ── */
const Doughnut3D = ({ data, width = 260, height = 200 }) => {
  const [tip, setTip] = useState(null);
  const ref = React.useRef(null);
  const cx = width / 2, cy = height / 2 - 8;
  const rx = width * 0.38, ry = height * 0.22;
  const irx = rx * 0.55, iry = ry * 0.55;
  const depth = 22;
  const total = data.reduce((a, c) => a + c.value, 0);
  if (total === 0) return null;

  const darken = (hex, amt) => {
    let c = parseInt(hex.replace('#', ''), 16);
    let r = Math.max((c >> 16) - amt, 0), g = Math.max(((c >> 8) & 0xff) - amt, 0), b = Math.max((c & 0xff) - amt, 0);
    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
  };

  const slices = [];
  let cumAngle = -Math.PI / 2;
  data.forEach((d, i) => {
    const angle = (d.value / total) * Math.PI * 2;
    slices.push({ ...d, startAngle: cumAngle, endAngle: cumAngle + angle, idx: i });
    cumAngle += angle;
  });

  const ellipsePoint = (a, erx, ery) => [cx + erx * Math.cos(a), cy + ery * Math.sin(a)];

  const arcPath = (sa, ea, outerRx, outerRy, innerRx, innerRy, yOff = 0) => {
    const [ox1, oy1] = ellipsePoint(sa, outerRx, outerRy);
    const [ox2, oy2] = ellipsePoint(ea, outerRx, outerRy);
    const [ix1, iy1] = ellipsePoint(ea, innerRx, innerRy);
    const [ix2, iy2] = ellipsePoint(sa, innerRx, innerRy);
    const large = ea - sa > Math.PI ? 1 : 0;
    return `M${ox1},${oy1 + yOff} A${outerRx},${outerRy} 0 ${large} 1 ${ox2},${oy2 + yOff} L${ix1},${iy1 + yOff} A${innerRx},${innerRy} 0 ${large} 0 ${ix2},${iy2 + yOff} Z`;
  };

  const onHover = (e, s) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: s.name, items: [{ name: 'Count', value: s.value, color: s.color }, { name: 'Share', value: Math.round((s.value / total) * 100) + '%' }] });
  };
  return (
    <div ref={ref} style={{ position: 'relative' }} onMouseLeave={() => setTip(null)}>
    <svg width={width} height={height + depth + 10} viewBox={`0 0 ${width} ${height + depth + 10}`} style={{ display: 'block', margin: '0 auto' }}>
      {/* Shadow */}
      <ellipse cx={cx} cy={cy + depth + 6} rx={rx + 4} ry={ry + 3} fill="rgba(0,0,0,0.06)" />
      {/* Side faces (3D depth) — only bottom-facing arcs */}
      {slices.map(s => {
        const midA = (s.startAngle + s.endAngle) / 2;
        if (Math.sin(midA) < -0.15) return null; // skip top-facing
        const clipSa = Math.max(s.startAngle, 0 - 0.01);
        const clipEa = Math.min(s.endAngle, Math.PI + 0.01);
        if (clipSa >= clipEa) return null;
        const [p1x, p1y] = ellipsePoint(clipSa, rx, ry);
        const [p2x, p2y] = ellipsePoint(clipEa, rx, ry);
        const large = clipEa - clipSa > Math.PI ? 1 : 0;
        return (
          <path key={`side-${s.idx}`}
            d={`M${p1x},${p1y} A${rx},${ry} 0 ${large} 1 ${p2x},${p2y} L${p2x},${p2y + depth} A${rx},${ry} 0 ${large} 0 ${p1x},${p1y + depth} Z`}
            fill={darken(s.color, 45)} opacity={0.85} />
        );
      })}
      {/* Inner wall */}
      <ellipse cx={cx} cy={cy + depth} rx={irx} ry={iry} fill="#e8e8e5" />
      <ellipse cx={cx} cy={cy} rx={irx} ry={iry} fill="#fff" />
      {/* Top face */}
      {slices.map(s => (
        <path key={`top-${s.idx}`} d={arcPath(s.startAngle, s.endAngle, rx, ry, irx, iry)}
          fill={s.color} stroke="#fff" strokeWidth={1.5} style={{ filter: 'brightness(1.05)', cursor: 'pointer' }}
          onMouseMove={e => onHover(e, s)} onMouseLeave={() => setTip(null)} />
      ))}
      {/* Center label */}
      <text x={cx} y={cy - 4} textAnchor="middle" style={{ fontSize: 20, fontWeight: 800, fill: '#0f172a', fontFamily: 'Poppins' }}>{total.toLocaleString()}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" style={{ fontSize: 9, fontWeight: 600, fill: '#94a3b8', letterSpacing: 1.2, textTransform: 'uppercase' }}>Total</text>
    </svg>
    <DoughnutTooltip tip={tip} />
    </div>
  );
};

const PieCenter = ({ cx, cy, label, value }) => (
  <g>
    <text x={cx} y={cy - 6} textAnchor="middle" style={{ fontSize: 20, fontWeight: 800, fill: '#0f172a', fontFamily: 'Poppins' }}>{value}</text>
    <text x={cx} y={cy + 14} textAnchor="middle" style={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase' }}>{label}</text>
  </g>
);

const PieLegend = ({ data }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 0', justifyContent: 'center' }}>
    {data.map((d, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, background: 'var(--bg-primary)' }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: '#334155' }}>{d.name}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#0f172a' }}>{d.value?.toLocaleString()}</span>
      </div>
    ))}
  </div>
);

const SectionHeader = ({ title, subtitle }) => (
  <div style={{ marginBottom: 14 }}>
    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>{title}</h3>
    {subtitle && <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0', fontWeight: 500 }}>{subtitle}</p>}
  </div>
);

const Gradients = ({ id }) => (
  <defs>
    <linearGradient id={`${id}GG`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#7ea52e" stopOpacity={0.35} />
      <stop offset="100%" stopColor="#7ea52e" stopOpacity={0.02} />
    </linearGradient>
    <linearGradient id={`${id}GR`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#1a1a18" stopOpacity={0.2} />
      <stop offset="100%" stopColor="#1a1a18" stopOpacity={0.02} />
    </linearGradient>
    <linearGradient id={`${id}BarG`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#9ec25a" /><stop offset="100%" stopColor="#7ea52e" />
    </linearGradient>
    <linearGradient id={`${id}BarR`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#333330" /><stop offset="100%" stopColor="#1a1a18" />
    </linearGradient>
    <linearGradient id={`${id}BarO`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#b7d083" /><stop offset="100%" stopColor="#9ec25a" />
    </linearGradient>
  </defs>
);

export default function UserDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/user/dashboard').then(({ data }) => { setData(data); setLoading(false); })
      .catch(() => { toast.error('Failed to load dashboard'); setLoading(false); });
  }, []);

  if (loading) return (
    <div className="loading-spinner">
      <div className="spinner" />
      <div className="loading-text">Loading...</div>
      <div className="loading-dots"><span /><span /><span /></div>
    </div>
  );

  const stats = data?.stats || {};
  const recentCampaigns = data?.recentCampaigns || [];
  const channelData = data?.channelData || [];

  /* Pie — Status */
  const pieData = [
    { name: 'Delivered', value: stats.totalSent || 0, color: C.green },
    { name: 'Failed', value: stats.totalFailed || 0, color: C.red },
    { name: 'Pending', value: stats.totalPending || 0, color: C.orange },
  ].filter(d => d.value > 0);
  if (pieData.length === 0) pieData.push({ name: 'No Data', value: 1, color: '#e6e6e3' });
  const pieTotal = pieData.reduce((a, c) => a + c.value, 0);

  /* Channel Pie */
  const channelPie = channelData.map(ch => ({
    name: TYPE_LABELS[ch.type] || ch.type,
    value: (ch.sent || 0) + (ch.failed || 0),
    color: TYPE_COLORS[ch.type] || C.slate
  })).filter(c => c.value > 0);
  if (channelPie.length === 0) channelPie.push({ name: 'No Data', value: 1, color: '#e6e6e3' });
  const channelTotal = channelPie.reduce((a, c) => a + c.value, 0);

  /* 3D Doughnut data for Message Status */
  const doughnutData = pieData;

  /* Channel Funnel — sorted descending for proper funnel shape */
  const CHANNEL_FUNNEL_COLORS = ['#5c7a1f', '#7ea52e', '#9ec25a', '#b7d083', '#d4e6a8', '#1a1a18', '#333330', '#7e7e71', '#abab9f', '#c9c9c3'];
  const channelFunnel = [...channelPie]
    .sort((a, b) => b.value - a.value)
    .map((ch, i) => ({ name: ch.name, value: ch.value, fill: CHANNEL_FUNNEL_COLORS[i % CHANNEL_FUNNEL_COLORS.length] }));

  /* Campaign Funnel — Sent/Failed/Pending breakdown */
  const campaignFunnel = [
    { name: 'Sent', value: stats.totalSent || 0, fill: '#7ea52e' },
    { name: 'Pending', value: stats.totalPending || 0, fill: '#9ec25a' },
    { name: 'Failed', value: stats.totalFailed || 0, fill: '#1a1a18' },
  ].filter(d => d.value > 0);
  if (campaignFunnel.length === 0) campaignFunnel.push({ name: 'No Data', value: 1, fill: '#e6e6e3' });

  /* Area Trend — pad so area renders even with 1–2 points */
  let trendData = recentCampaigns.slice(0, 7).map((c, i) => ({
    name: c.name?.substring(0, 10) || `C${i + 1}`,
    Delivered: c.mySent || 0, Failed: c.myFailed || 0,
  }));
  if (trendData.length === 1) {
    trendData = [
      { name: '', Delivered: 0, Failed: 0 },
      trendData[0],
      { name: ' ', Delivered: 0, Failed: 0 },
    ];
  } else if (trendData.length === 2) {
    trendData = [{ name: '', Delivered: 0, Failed: 0 }, ...trendData];
  }

  /* Pyramid data — smallest at top (narrow), largest at bottom (wide) */
  const pyramidData = [
    { name: 'Failed', value: stats.totalFailed || 0, fill: '#1a1a18' },
    { name: 'Pending', value: stats.totalPending || 0, fill: '#9ec25a' },
    { name: 'Delivered', value: stats.totalSent || 0, fill: '#7ea52e' },
    { name: 'Assigned', value: stats.totalAssigned || 0, fill: '#5c7a1f' },
  ].filter(d => d.value > 0);
  if (pyramidData.length === 0) pyramidData.push({ name: 'No Data', value: 1, fill: '#e6e6e3' });

  const totalMessages = (stats.totalSent || 0) + (stats.totalFailed || 0) + (stats.totalPending || 0);
  const successRate = totalMessages > 0 ? ((stats.totalSent / totalMessages) * 100).toFixed(1) : '0';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Overview</h1>
          <p className="page-subtitle">Your broadcast performance</p>
        </div>
        <div style={{
          padding: '8px 18px', borderRadius: 12,
          background: 'linear-gradient(135deg, #eef3e6, #e5edda)',
          display: 'flex', alignItems: 'center', gap: 6,
          animation: 'popIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.3s both',
          transition: 'all 0.3s ease',
          cursor: 'default',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(126,165,46,0.2)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}>
          <FiArrowUpRight size={14} color={C.green} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#455a19' }}>{successRate}% Success</span>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="stats-grid">
        {[
          { icon: <FiSend />, value: stats.totalCampaigns || 0, label: 'My Broadcasts', cls: 'purple', accent: '#1a1a18' },
          { icon: <FiClock />, value: stats.totalAssigned || 0, label: 'Total Assigned', cls: 'blue', accent: '#7ea52e' },
          { icon: <FiCheckCircle />, value: stats.totalSent || 0, label: 'Delivered', cls: 'green', accent: '#5c7a1f' },
          { icon: <FiXCircle />, value: stats.totalFailed || 0, label: 'Failed', cls: 'red', accent: '#1a1a18' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ borderBottom: `3px solid ${s.accent}`, animationDelay: `${0.05 + i * 0.08}s` }}
            onMouseMove={e => { const r = e.currentTarget.getBoundingClientRect(); e.currentTarget.style.setProperty('--mouse-x', `${((e.clientX - r.left) / r.width) * 100}%`); e.currentTarget.style.setProperty('--mouse-y', `${((e.clientY - r.top) / r.height) * 100}%`); }}>
            <div className={`stat-card-icon ${s.cls}`}>{s.icon}</div>
            <div className="stat-card-value"><AnimatedNumber value={s.value} duration={1000 + i * 200} /></div>
            <div className="stat-card-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Row 1: Spiral + Polar + Pyramid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="chart-container">
          <SectionHeader title="Message Status" subtitle="Delivery breakdown" />
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0' }}>
            <Doughnut3D data={doughnutData} width={280} height={180} />
          </div>
          <PieLegend data={pieData} />
        </div>

        <div className="chart-container">
          <SectionHeader title="Channel Breakdown" subtitle="By campaign type" />
          <ResponsiveContainer width="100%" height={230}>
            <FunnelChart margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <Tooltip content={<GlassTooltip />} />
              <Funnel dataKey="value" data={channelFunnel} isAnimationActive animationDuration={1200} animationEasing="ease-in-out">
                {channelFunnel.map((e, i) => <Cell key={i} fill={e.fill} stroke="#fff" strokeWidth={2} />)}
                <LabelList position="center" dataKey="name" style={{ fill: '#fff', fontSize: 12, fontWeight: 700, fontFamily: 'Poppins, sans-serif', textShadow: '0 1px 3px rgba(0,0,0,0.35)' }} />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
          <PieLegend data={channelPie} />
        </div>

        <div className="chart-container">
          <SectionHeader title="Message Pyramid" subtitle={`${successRate}% success rate`} />
          <ResponsiveContainer width="100%" height={250}>
            <FunnelChart margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
              <Tooltip content={<GlassTooltip />} />
              <Funnel dataKey="value" data={pyramidData} isAnimationActive animationDuration={1400} animationEasing="ease-in-out">
                {pyramidData.map((entry, i) => <Cell key={i} fill={entry.fill} stroke="#fff" strokeWidth={2} />)}
                <LabelList position="center" dataKey="name" style={{ fill: '#fff', fontSize: 12, fontWeight: 700, fontFamily: 'Poppins, sans-serif', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
          <PieLegend data={pyramidData.map(d => ({ name: d.name, value: d.value, color: d.fill }))} />
        </div>
      </div>

      {/* Row 2: Delivery Trend — Smooth Stacked Area Chart */}
      <div className="chart-container" style={{ marginBottom: 20 }}>
        <SectionHeader title="Delivery Trend" subtitle="Performance across campaigns" />
        {trendData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="areaDelivered" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7ea52e" stopOpacity={0.45} />
                  <stop offset="50%" stopColor="#9ec25a" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#b7d083" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="areaFailed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1a1a18" stopOpacity={0.35} />
                  <stop offset="50%" stopColor="#333330" stopOpacity={0.12} />
                  <stop offset="100%" stopColor="#1a1a18" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 8" stroke="#e9ecef" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<GlassTooltip />} />
              <Legend content={<CustomLegend />} />
              <Area type="natural" dataKey="Delivered" stackId="1" stroke="#7ea52e" fill="url(#areaDelivered)" strokeWidth={3}
                dot={{ r: 5, fill: '#fff', stroke: '#7ea52e', strokeWidth: 2.5 }}
                activeDot={{ r: 8, fill: '#7ea52e', stroke: '#fff', strokeWidth: 3 }}
                animationDuration={1200} animationEasing="ease-in-out" />
              <Area type="natural" dataKey="Failed" stackId="1" stroke="#1a1a18" fill="url(#areaFailed)" strokeWidth={2.5}
                dot={{ r: 4, fill: '#fff', stroke: '#1a1a18', strokeWidth: 2 }}
                activeDot={{ r: 7, fill: '#1a1a18', stroke: '#fff', strokeWidth: 3 }}
                animationDuration={1500} animationEasing="ease-in-out" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="empty-state"><div className="empty-state-icon">📈</div><h3>No trend data yet</h3></div>
        )}
      </div>

      {/* Row 3: Campaign Breakdown — Funnel Chart */}
      {campaignFunnel.length > 0 && (
        <div className="chart-container" style={{ marginBottom: 20 }}>
          <SectionHeader title="Campaign Breakdown" subtitle="Sent vs Failed vs Pending" />
          <ResponsiveContainer width="100%" height={280}>
            <FunnelChart margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
              <Tooltip content={<GlassTooltip />} />
              <Funnel dataKey="value" data={campaignFunnel} isAnimationActive animationDuration={1400} animationEasing="ease-in-out">
                {campaignFunnel.map((e, i) => <Cell key={i} fill={e.fill} stroke="#fff" strokeWidth={2} />)}
                <LabelList position="right" content={({ x, y, width, height, value, name }) => (
                  <text x={x + width + 8} y={y + height / 2} textAnchor="start" dominantBaseline="central" style={{ fontSize: 13, fontWeight: 700, fill: '#0f172a', fontFamily: 'Poppins, sans-serif' }}>
                    {name}: {value?.toLocaleString()}
                  </text>
                )} />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
          <PieLegend data={campaignFunnel.map(d => ({ name: d.name, value: d.value, color: d.fill }))} />
        </div>
      )}

      {/* Recent Campaigns Table */}
      {recentCampaigns.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <SectionHeader title="Recent Broadcasts" subtitle="Your latest campaign data" />
            <button className="btn btn-secondary" onClick={() => navigate('/user/campaigns')} style={{ fontSize: 12, padding: '6px 16px', whiteSpace: 'nowrap' }}>
              View All →
            </button>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr><th>Campaign</th><th>Type</th><th>Assigned</th><th>Sent</th><th>Failed</th><th>Pending</th><th>Status</th></tr>
              </thead>
              <tbody>
                {recentCampaigns.map(c => (
                  <tr key={c._id} style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                    onClick={() => navigate(`/user/campaigns/${c._id}`)}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(126,165,46,0.05)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ fontWeight: 600, color: '#7ea52e' }}>{c.name}</td>
                    <td><span className="badge badge-info" style={{ fontSize: 10 }}>{TYPE_LABELS[c.type] || c.type}</span></td>
                    <td>{c.myAssigned}</td>
                    <td style={{ color: '#7ea52e', fontWeight: 700 }}>{c.mySent}</td>
                    <td style={{ color: '#1a1a18', fontWeight: 700 }}>{c.myFailed}</td>
                    <td style={{ color: '#9ec25a', fontWeight: 700 }}>{c.myPending}</td>
                    <td><span className={`badge badge-${c.status}`}>{c.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
